import {useEffect, useState} from 'react';
import {isMobileView} from '@/lib/picos/browserFns';

/**
 * Reactive wrapper around `isMobileView()`.  Re-renders the calling
 * component when the window crosses the mobile/desktop boundary.
 *
 * The non-reactive `isMobileView()` returns a snapshot at the moment of
 * the call; a component that uses it inside its render (or its column-
 * definition memos) won't update when the user resizes the browser
 * across the threshold.  This hook closes that gap by subscribing to
 * `window.resize` and updating local state only when the boolean
 * actually flips (React bails out on identical-state setState, so
 * resize events that don't cross the threshold cost nothing).
 *
 * Mirrors `isMobileView`'s `overrideWidth` argument: pass an explicit
 * width to use instead of the default `MOBILE_WIDTH`.
 */
export function useIsMobileView(overrideWidth?: number): boolean {
    const [isMobile, setIsMobile] = useState(() => isMobileView(overrideWidth));

    useEffect(() => {
        const update = () => setIsMobile(isMobileView(overrideWidth));
        window.addEventListener('resize', update);
        // Catch any change that occurred between the useState initializer
        // running and this effect attaching the listener.
        update();
        return () => window.removeEventListener('resize', update);
    }, [overrideWidth]);

    return isMobile;
}
